import {
  ChangeDetectionStrategy,
  ChangeDetectorRef,
  Component,
  EventEmitter,
  HostBinding,
  inject,
  Input,
  Output,
  signal,
} from '@angular/core';
import { UntilDestroy, untilDestroyed } from '@ngneat/until-destroy';
import { Actions, ofType } from '@ngrx/effects';
import { Store } from '@ngrx/store';
import { produce } from 'immer';
import {
  BehaviorSubject,
  combineLatest,
  distinctUntilChanged,
  filter,
  map,
  Observable,
  of,
  skipWhile,
  startWith,
  switchMap,
  take,
} from 'rxjs';

import { runtimeActions, runtimeSelectors } from '@blaze-runtime/engine-data-access';
import {
  type CanvasElementBindings,
  CanvasElementDataInput,
  type CanvasElementDataInputPayload,
  CanvasElementEvent,
  CanvasElementFrom,
  type CanvasElementId,
  CanvasElementType,
  type IoConfig,
  PropsOf,
  TagConfig,
  TagConfigQuery,
} from '@blaze-runtime/engine-domain';
import { DataInputFacade } from '@blaze-runtime/engine-domain';
import { ProjectContextFacade } from '@blaze-shared/app-state-data-access';
import { UserAccessRolesService } from '@blaze-shared/auth-data-access';
import {
  ButtonElementMetaUiFeatures,
  CanvasElementInputBinding,
  ContainerElementMetaUiFeatures,
  DataBindingSourceInfo,
  ExpressionObj,
  InputElementMetaUiFeatures,
  PropertiesConfigInputContainerItemActionModel,
  PropertiesConfigInputListColumnModel,
  RenderElementContext,
  RuntimeContext,
  ViewportMode,
} from '@blaze-shared/elements-domain';
import { IntelliSenseService } from '@blaze-shared/intellisense-feature';
import { SchemaService } from '@blaze-shared/schema-data-access';
import { findColumn, getColumnKey } from '@blaze-shared/schema-domain';
import {
  BaseSchema,
  BlazeSchema,
  createElementSchema,
  DatasetColumn,
  DatasetRow,
  DatasetSchema,
  type ElementSchema,
  type RuntimeEventType,
} from '@blaze-shared/schema-domain';
import { countedShareReplay, isDeepEqual, isEqual, isNullish } from '@blaze-shared/shared-util';
import { fromStyles } from '@blaze-shared/style-state-feature';
import { FontsService, ThemesService } from '@blaze-shared/theming-data-access';

import { getElementById } from '../../+state/elements/elements.selectors';
import {
  CanvasElementComponent,
  type ComponentSlotData,
} from '../../canvas-elements/element-component';
import { arePropsWithVisibility } from '../../canvas-elements/element-model/are-props-with-visibility';
import { ElementInputProviderService } from '../../canvas-elements/io-data/inputs/element-input-data-provider.service';
import { ElementDataTypeActionsService } from '../../services/element-data-type-actions.service';
import { ElementDefinitionsService } from '../../services/element-definitions.service';
import { ElementStoreViewService } from '../../services/element-store-view.service';
import { ElementTagsService } from '../../services/element-tags.service';
import { ElementStylesService } from '../../services/styles';
import { StylesCleanup } from '../../services/styles-cleanup.service';
import { ViewportService } from '../../services/viewport.service';
import { evaluateExpression } from '../../utils/elements/expressions.utils';
import { getUiRuntimeElement } from '../../utils/runtime';

/** Decorator creating keeping the value in a private field and resolving the column's name when reading it (getter). */
export function ColumnIdOrName() {
  return function (proto: any, originalKey: string): void {
    const privateKey = `__${originalKey}`;
    Object.defineProperty(proto, originalKey, {
      get() {
        return this.getColumnName(this[privateKey]) || '';
      },
      set(this, value) {
        this[privateKey] = value;
      },
    });
  };
}

export interface TriggerUpdatePayload {
  emptyRow?: DatasetRow;
  /** Update the options list only (used to refresh the list after manual selection change) */
  optionsListOnly?: boolean;
}

@UntilDestroy()
@Component({
  template: '',
  changeDetection: ChangeDetectionStrategy.OnPush,
})
export abstract class CanvasElementBaseComponent<T extends CanvasElementType>
  implements CanvasElementComponent<T>
{
  /** intellisense should not be used directly in the components */
  private readonly elementStoreView = inject(ElementStoreViewService);
  private readonly intellisense = inject(IntelliSenseService);
  private readonly viewport = inject(ViewportService);

  protected readonly actions$ = inject(Actions);
  protected readonly cdr = inject(ChangeDetectorRef);
  protected readonly dataTypeActions = inject(ElementDataTypeActionsService);
  protected readonly elementDefinitionsService = inject(ElementDefinitionsService);
  protected readonly elementStylesService = inject(ElementStylesService);
  protected readonly elementTagsService = inject(ElementTagsService);
  protected readonly fontsService = inject(FontsService);
  protected readonly projectContextFacade = inject(ProjectContextFacade);
  protected readonly schemaService = inject(SchemaService);
  protected readonly store = inject(Store);
  protected readonly stylesCleanup = inject(StylesCleanup);
  protected readonly styleSheetsFacade = inject(fromStyles.StyleSheetsFacade);
  protected readonly themesService = inject(ThemesService);
  protected readonly userAccessRolesService = inject(UserAccessRolesService);

  @Input() elementType!: T;
  @Input() ns?: CanvasElementId;
  @Input() bindingResults: CanvasElementBindings = {};
  @Input() ioConfig?: IoConfig;
  @Input() isInEditor = false;
  @Input() slots?: ComponentSlotData<T>;
  // Fixme [MG] It can be an array of whatever "visible" data source will provide
  @Input() visible?: boolean | any[];
  @Input() viewportMode: ViewportMode | undefined;

  @Output() output = new EventEmitter<CanvasElementEvent>();

  private readonly elementId$$ = new BehaviorSubject<CanvasElementId>('');
  private readonly elementContext$$ = new BehaviorSubject<RenderElementContext | undefined>(
    undefined,
  );
  private readonly props$$ = new BehaviorSubject<PropsOf<T> | undefined>(undefined);
  private readonly input$$ = new BehaviorSubject<CanvasElementDataInput | undefined>(undefined);
  private readonly elementSchemaSet$$ = new BehaviorSubject<
    Partial<Record<RuntimeEventType, boolean>>
  >({});

  private schemaRegistrationRequest?: Partial<
    Record<RuntimeEventType, { eventType: RuntimeEventType; schema: ElementSchema }>
  >;
  private elementStateReady = false;

  private readonly elementMargin = signal<string | undefined>(undefined);

  protected getActionClasses = this.elementStylesService.getActionClasses;

  /** Canvas element as it is stored in the state */
  readonly element$!: Observable<CanvasElementFrom<T>>;
  readonly elementId$ = this.elementId$$.asObservable();
  /** Render element context **/
  readonly elementContext$: Observable<RenderElementContext | undefined>;
  /** Current schemas associated with events dispatched by the component */
  readonly eventSchemas = new Map<RuntimeEventType, ElementSchema | null | undefined>();
  /** Props with the default values applied */
  readonly props$!: Observable<PropsOf<T> | undefined>;
  /** Props with the default values applied */
  readonly input$!: Observable<PropsOf<T> | undefined>;
  /** Element input data is pending */
  readonly isPending$!: Observable<boolean>;
  /** Runtime context for the element */
  readonly runtimeContext$!: Observable<RuntimeContext>;
  /** Viewport mode */
  readonly viewportMode$ = this.viewport.mode$;
  /** Navigation header height */
  readonly navHeaderHeight$ = this.viewport.navHeaderHeight$;
  /** Navigation bottombar height */
  readonly navBottombarHeight$ = this.viewport.navBottombarHeight$;
  readonly dataInput$ = this.input$$.asObservable();

  @Input() set elementId(value: CanvasElementId | undefined) {
    if (!value) {
      throw new Error('Element id missing');
    }
    this.elementId$$.next(value);
  }
  get elementId(): CanvasElementId {
    return this.elementId$$.value;
  }

  @Input() set elementContext(value: RenderElementContext | undefined) {
    this.elementContext$$.next(value);
  }
  get elementContext(): RenderElementContext | undefined {
    return this.elementContext$$.value;
  }

  @Input() set props(value: PropsOf<T> | undefined) {
    this.props$$.next(value);
  }
  /** Props with the default values applied */
  get props(): PropsOf<T> | undefined {
    return this.props$$.value;
  }

  @Input() set dataInput(payload: CanvasElementDataInputPayload) {
    this.input$$.next(
      produce(this.input$$.value ?? {}, (draft) => {
        draft[payload.key] = payload;
      }),
    );
  }

  @HostBinding('class.mobile') get isMobile(): boolean {
    return this.viewportMode === 'mobile';
  }
  @HostBinding('class.tablet') get isTablet(): boolean {
    return this.viewportMode === 'tablet';
  }
  @HostBinding('class.be-hidden-editor') get isHiddenEditor(): boolean {
    if (!this.isInEditor) {
      return false;
    }
    if (this.bindingResults?.['visible'] && !this.visible) {
      return true;
    }
    const props = this.props;
    if (arePropsWithVisibility(props) && props.content?.visible?.accessRoles?.length) {
      return !this.userAccessRolesService.isCurrentUserInAccessRoles(
        props.content.visible.accessRoles,
      );
    }

    return false;
  }
  /**
   * Rendered as host padding because canvas elements are using padding to simulate the margin.
   * Editor relies on the fact that all elements are contained within a container.
   */
  @HostBinding('style.padding') get hostMargin(): string | undefined {
    return this.elementMargin();
  }
  protected get isUsingOldPaddingStrategy(): boolean {
    return !this.themesService.activeTheme?.value.isSpacingDefined;
  }

  constructor() {
    // Internally used services - should not be used directly in the component
    const elementInputProviderService = inject(ElementInputProviderService);

    this.element$ = this.elementId$.pipe(
      switchMap(
        (elementId) =>
          this.store.select(getElementById(elementId)) as Observable<
            CanvasElementFrom<T> | undefined
          >,
      ),
      filter(Boolean),
    );

    this.element$.pipe(untilDestroyed(this)).subscribe((element) => {
      this.elementStateReady = !!element;
      if (element && this.schemaRegistrationRequest) {
        for (const { eventType, schema } of Object.values(this.schemaRegistrationRequest)) {
          this.setDefaultEventSchema(eventType, schema);
        }
        this.schemaRegistrationRequest = undefined;
      }
    });
    this.elementContext$ = this.elementContext$$.asObservable();
    this.props$ = this.props$$.asObservable();
    this.isPending$ = this.elementId$.pipe(
      switchMap((elementId) =>
        this.store.select(runtimeSelectors.selectIsPendingUiByElementId(elementId)),
      ),
    );
    this.runtimeContext$ = this.element$.pipe(
      // Recalculating runtime context can be an expensive operation -> react just to id and type changes
      map(({ id, type }) => ({ id, type })),
      distinctUntilChanged(isDeepEqual),
      switchMap(({ id, type }) => {
        const elementDefinition = this.elementDefinitionsService.getDefinition(type);
        if (!elementDefinition) {
          throw Error(`Missing element definition for: ${id}`);
        }
        return elementInputProviderService.getExpressionRuntimeContext(id, type);
      }),
      distinctUntilChanged(isEqual),
      countedShareReplay(),
    );
  }

  /**
   * @param eventType
   * @returns Default element schema (when no schema can be calculated).
   * Note: always use @see createElementSchema when overriding this method.
   */
  getDefaultEventSchema(eventType: RuntimeEventType): ElementSchema | null {
    const output = this.ioConfig?.outputs?.find((output) => output.eventType === eventType);
    const defaultSchema = output?.schema;
    if (isNullish(defaultSchema)) {
      return null;
    }
    return this.createElementSchema(
      eventType,
      defaultSchema?.columns || [],
      undefined,
      defaultSchema,
    );
  }

  /**
   * Stores default schema to ensure it's available when the element is dropped in the editor. This is required because
   * there are elements (unbound) which when they are dropped don't always emit ui event with the schema.
   * @param eventType
   */
  protected setDefaultEventSchema(
    eventType: RuntimeEventType,
    schema?: ElementSchema | null,
  ): void {
    schema = schema || this.getDefaultEventSchema?.(eventType);
    if (!schema) {
      return;
    }

    if (!this.elementStateReady) {
      this.schemaRegistrationRequest = {
        ...this.schemaRegistrationRequest,
        [eventType]: { eventType, schema },
      };
      return;
    }

    this.setEventSchema(eventType, schema);
    this.elementSchemaSet$$.next({ ...this.elementSchemaSet$$.value, [eventType]: false });

    this.actions$
      .pipe(
        ofType(runtimeActions.storeRuntimeElementSchema),
        filter((action) => action.element.id === this.elementId && action.eventType === eventType),
        take(1),
      )
      .subscribe(() => {
        this.elementSchemaSet$$.next({ ...this.elementSchemaSet$$.value, [eventType]: true });
      });
    this.store.dispatch(
      runtimeActions.storeRuntimeElementSchema({
        element: getUiRuntimeElement(this.elementId),
        schema,
        isPaginated: false,
        eventType,
      }),
    );
  }

  protected getEventSchema(eventType: RuntimeEventType): ElementSchema | undefined {
    return this.eventSchemas.get(eventType) ?? undefined;
  }

  protected setEventSchema(eventType: RuntimeEventType, schema?: ElementSchema | null): void {
    this.eventSchemas.set(eventType, schema);
  }

  /**
   * Creates and sets the schema for the given event type.
   * @param eventType - Type of the event which sends data of the created schema
   * @param dataInput  - Data input from which we get necessary information to create schema
   * @param outputsArray - Whether we expect the data to return an array instead of an object
   * @returns
   */
  protected setEventSchemaFromDataInput(
    eventType: RuntimeEventType,
    dataInput: DataInputFacade<CanvasElementInputBinding>,
    isOutputDataArray = false,
  ): void {
    // For selected data input if there's a column selected, set schema to the non-object value of that column.
    // TODO(michal): This currently doesn't handle Array and Object columns. Implement.
    if (dataInput.input === 'selected' && dataInput.schema && dataInput.column) {
      // If a column is a multiple values column, outputed data will be an array of non-object values
      isOutputDataArray = isOutputDataArray || !!dataInput.column.multipleValues;

      const selectedValueSchema = this.createElementSchema(
        'change',
        [{ ...dataInput.column }],
        undefined,
        { id: '', name: dataInput.schema.name, columns: [] },
        isOutputDataArray,
      );
      this.setDefaultEventSchema(eventType, selectedValueSchema);
      return;
    }

    // For everything else, use dataInput schema to create ElementSchema
    const eventSchema = this.createElementSchema(
      eventType,
      undefined,
      dataInput.schema,
      undefined,
      isOutputDataArray,
    );
    if (eventSchema) {
      this.setDefaultEventSchema(eventType, eventSchema);
    }
  }

  protected createElementSchema(
    eventType: RuntimeEventType,
    columns?: DatasetColumn[],
    dataSourceSchema?: BlazeSchema | null /* schema of the data bound to the element */,
    defaultSchema?: BlazeSchema | null,
    isOutputDataArray = false,
  ): ElementSchema {
    const content: any = this.props && 'content' in this.props ? this.props.content : undefined;
    return createElementSchema(
      eventType,
      this.elementId,
      columns,
      dataSourceSchema,
      defaultSchema,
      isOutputDataArray,
      content?.title?.label || this.elementType,
    );
  }

  protected evaluateExpression(
    expression: any,
    context: RuntimeContext,
    ignoreNormalization = false,
    isVisibility = true,
  ): any {
    return evaluateExpression({
      expression,
      context,
      intellisense: this.intellisense,
      elementId: this.elementId,
      elementType: this.elementType,
      ignoreNormalization,
      isVisibility,
    });
  }

  protected queryExpression(value: any, expression: any, context: RuntimeContext): any {
    return this.intellisense.queryExpressionObj(value, expression, context);
  }

  protected normalizeLinkedParams(context: RuntimeContext): RuntimeContext {
    return this.intellisense.normalizeLinkedParams(context);
  }

  /** Returns ID column name for given data source */
  protected getIdColumnName(dataSource: DataBindingSourceInfo | undefined): string | undefined {
    return (
      dataSource && this.schemaService.getDataSourceIdColumn(dataSource.id, dataSource.sourceType)
    );
  }

  /** Maps the column field to name if column ID is given */
  protected getColumnName(columnIdOrName: string | undefined): string | undefined {
    return columnIdOrName && this.schemaService.getColumnName(columnIdOrName);
  }

  protected getDataInput<T extends CanvasElementInputBinding>(input: T): DataInputFacade<T> {
    const input$$ = this.input$$;
    const dataInput$ = this.input$$.pipe(
      map((i) => i?.[input]),
      distinctUntilChanged(isEqual),
      countedShareReplay(),
    );

    return {
      input,
      dataInput$,
      data$: dataInput$.pipe(
        skipWhile((input) => input === undefined), // skip the initial subject value
        map((i) => i?.data),
        distinctUntilChanged(isEqual),
      ),
      schema$: dataInput$.pipe(
        map((i) => i?.schema),
        distinctUntilChanged(isEqual),
        countedShareReplay(),
      ),
      column$: dataInput$.pipe(
        map((i) => i?.column),
        distinctUntilChanged(isEqual),
        countedShareReplay(),
      ),
      get schema(): BlazeSchema | undefined {
        return input$$.value?.[input]?.schema;
      },
      get column(): DatasetColumn | undefined {
        return input$$.value?.[input]?.column;
      },
    };
  }

  protected getDataInputValue<T extends CanvasElementInputBinding>(
    input: T,
  ): CanvasElementDataInputPayload<T> | undefined {
    return this.input$$.value?.[input];
  }

  protected getElementUiFeatures<
    TMeta extends
      | InputElementMetaUiFeatures
      | ButtonElementMetaUiFeatures
      | ContainerElementMetaUiFeatures,
  >(): Observable<TMeta> {
    return this.store.select(runtimeSelectors.selectElementMetaById(this.elementId)).pipe(
      map((meta) => meta.uiFeatures as TMeta),
      filter(Boolean),
    );
  }

  protected getElementEventSchemaSet(eventType: RuntimeEventType): Observable<boolean> {
    return this.elementSchemaSet$$.pipe(
      map((ess) => ess[eventType]),
      filter(Boolean),
    );
  }

  protected getRequiredSuffix(features: { isRequired: boolean; showIsRequired: boolean }): string {
    return features.isRequired && features.showIsRequired ? ' *' : '';
  }

  protected createTagConfig(
    fields: Observable<TagConfigQuery | undefined>,
    columns: Observable<PropertiesConfigInputListColumnModel[] | undefined>,
    schema: Observable<BaseSchema | DatasetSchema | undefined>,
    data: Observable<undefined>,
  ): Observable<TagConfig> {
    let themeChanges = 0;
    return combineLatest({
      fields,
      columns,
      schema,
      data,
      // Needed only to trigger changes
      // Mapped to integer to avoid unnecessary deep object comparisons
      themeChanged: this.themesService.activeTheme$.pipe(map(() => (themeChanges += 1))),
    }).pipe(
      untilDestroyed(this),
      filter(({ fields, columns, schema }) => !!fields && !!columns && !!schema),
      distinctUntilChanged(isDeepEqual),
      map(({ fields, columns, schema, data }) => {
        // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
        return fields!.map((field) => {
          const column = columns?.find((col) => getColumnKey(col) === field.column);
          const columnName = this.getColumnName(field.column) ?? column?.name;
          const tagsDefinition = findColumn(schema?.columns, column)?.tagDefinition;
          const values = tagsDefinition
            ? tagsDefinition.items.map(({ value }) => value)
            : data && columnName
              ? (data as any[]).map?.((r) => r[columnName])
              : undefined;

          if (!column) {
            return undefined;
          }

          const palette =
            column.format === 'tag' || field.displayFormat === 'tag'
              ? this.elementTagsService.mapColumnValuesToColorPalette(
                  {
                    ...column,
                    tagsPalette: field.tagsPalette ?? column.tagsPalette,
                  },
                  schema,
                  values,
                )
              : undefined;

          return {
            id: column?.id,
            name: column.name,
            palette,
            order: values && Object.fromEntries(values.map((v, i) => [v, i])),
          };
        });
      }),
    );
  }

  protected createPaletteSchemaObservable(
    dataSource$: Observable<DataBindingSourceInfo | undefined>,
    bindingKey: CanvasElementInputBinding = 'value',
  ): Observable<DatasetSchema | undefined> {
    return this.elementTagsService.createSchemaObservable(dataSource$).pipe(
      startWith(undefined),
      filter((schema) => {
        // Allow palettes creation when element has no datasource bound
        if (!this.bindingResults?.[bindingKey]) {
          return true;
        }
        // Prevent schema disappear when search and filter updates schema
        return !!schema;
      }),
      distinctUntilChanged((a, b) => isEqual(a?.columns, b?.columns)),
    );
  }

  protected initActionStyles(
    elementActionsKey: string,
    elementActions$: Observable<PropertiesConfigInputContainerItemActionModel[]>,
    buttonClasses: string[],
  ): void {
    this.elementStylesService
      .observeActionsStyles(elementActionsKey, elementActions$, buttonClasses)
      .pipe(untilDestroyed(this))
      .subscribe();
  }

  /**
   * Keeps the element margin in sync with props, falling back to the theme spacing.
   * @param sourceFn - props mapper function used to override source of the margin
   * @protected
   */
  protected initMarginHandler(
    sourceFn?: (value: PropsOf<T> | undefined) => string | undefined,
  ): void {
    if (!sourceFn) {
      this.themesService.activeTheme$.pipe(untilDestroyed(this)).subscribe((theme) => {
        this.elementMargin.set(theme?.value.spacingExternal);
      });
      return;
    }

    combineLatest({
      theme: this.themesService.activeTheme$,
      source: this.props$.pipe(map(sourceFn), distinctUntilChanged()),
    })
      .pipe(
        untilDestroyed(this),
        map(({ theme, source }) => source ?? theme?.value.spacingExternal),
      )
      .subscribe((margin) => {
        this.elementMargin.set(margin);
      });
  }

  protected visibilityComponentScope(
    getter: (props: PropsOf<T>) => {
      enabled: boolean | undefined;
      expression: ExpressionObj[] | undefined;
    },
  ): Observable<boolean> {
    return this.props$$.pipe(
      filter(Boolean),
      switchMap((props) => {
        const { enabled, expression } = getter(props);
        return enabled
          ? this.runtimeContext$.pipe(
              map((context) => this.queryExpression('show', expression, context)),
            )
          : of(false);
      }),
      untilDestroyed(this),
    );
  }
}
